import { Box, Button, Card, Chip, Divider, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { UseQueryResult } from "@tanstack/react-query";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { GetPrivateInsuranceDetailsForLeaseResponse } from "@safelease/service-utilities";
import { Formatters } from "@safelease/service-utilities";
import { usePrivateInsuranceEphemeralUrl } from "../../usePrivateInsuranceEphemeralUrl";

type PrivateInsuranceDetailsProps = {
  privateInsuranceDetailsQuery: UseQueryResult<GetPrivateInsuranceDetailsForLeaseResponse>;
  leaseId: string;
};

function PrivateInsuranceDetails({ privateInsuranceDetailsQuery, leaseId }: PrivateInsuranceDetailsProps) {
  const { data: privateInsuranceDetails, isError } = privateInsuranceDetailsQuery;
  const { refetch } = usePrivateInsuranceEphemeralUrl(leaseId);

  if (!privateInsuranceDetails || isError) {
    enqueueSnackbar("Failed to load private insurance details", { variant: "error" });
    return null;
  }

  if (Object.keys(privateInsuranceDetails).length === 0) {
    // Some leases don't have a policy associated with them
    return null;
  }

  const handleViewPolicy = async () => {
    const { data } = await refetch();
    if (data) window.open(data, "_blank", "noopener,noreferrer");
  };

  return (
    <Card sx={{ padding: 2 }}>
      <Stack spacing={1}>
        <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"space-between"}>
          <Typography sx={{ fontSize: 18 }}>Private Policy</Typography>
          <Button onClick={handleViewPolicy} variant="text" color="primary">
            View Policy
          </Button>
        </Stack>
        <Divider />
        <Stack spacing={1}>
          <Box>
            {getStatusChipForPrivatePolicyValidationStatus(
              privateInsuranceDetails.validationStatus as "rejected" | "accepted" | "pending" | "humanReview" | "expired" | "archived",
            )}
          </Box>
          <Typography>
            <span style={{ color: "#667085" }}>Tenant Name</span> {privateInsuranceDetails.tenantName}
          </Typography>
          <Typography>
            <span style={{ color: "#667085" }}>Provider</span> {privateInsuranceDetails.provider ? privateInsuranceDetails.provider : "N/A"}
          </Typography>
          <Typography>
            <span style={{ color: "#667085" }}>Policy #</span>{" "}
            {privateInsuranceDetails.policyNumber ? privateInsuranceDetails.policyNumber : "N/A"}
          </Typography>
          <Typography>
            <span style={{ color: "#667085" }}>Policy Deductible</span>{" "}
            {privateInsuranceDetails.policyDeductibleCents !== null
              ? Formatters.penniesToDollars(privateInsuranceDetails.policyDeductibleCents)
              : "N/A"}
          </Typography>
          <Typography>
            <span style={{ color: "#667085" }}>Expiration date</span>{" "}
            {privateInsuranceDetails.expirationDate ? dayjs(privateInsuranceDetails.expirationDate).format("MM/DD/YY") : "N/A"}
          </Typography>
          <Typography>
            <span style={{ color: "#667085" }}>Upload date</span>{" "}
            {privateInsuranceDetails.createdAt ? dayjs(privateInsuranceDetails.createdAt).format("MM/DD/YY") : "N/A"}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );
}

function getStatusChipForPrivatePolicyValidationStatus(
  status: "rejected" | "accepted" | "pending" | "humanReview" | "expired" | "archived",
) {
  type StatusConfig = {
    label: string;
    color: string;
    bgcolor: string;
  };

  const statusConfigs: {
    [key: string]: StatusConfig;
  } = {
    accepted: {
      label: "Accepted",
      color: "white",
      bgcolor: "rgb(37, 155, 36)",
    },
    rejected: {
      label: "Rejected",
      color: "white",
      bgcolor: "rgb(233,100,95)",
    },
    expired: {
      label: "Expired",
      color: "rgb(3, 30, 48)",
      bgcolor: grey[100],
    },
    pending: {
      label: "Pending",
      color: "rgb(3, 30, 48)",
      bgcolor: "rgb(255, 195, 0)",
    },
    humanReview: {
      label: "Pending",
      color: "rgb(3, 30, 48)",
      bgcolor: "rgb(255, 195, 0)",
    },
    archived: {
      label: "Archived",
      color: "rgb(3, 30, 48)",
      bgcolor: grey[100],
    },
  };

  const { label, ...sx }: StatusConfig = statusConfigs[status];

  return <Chip size="small" label={label} sx={{ ...sx, fontWeight: 600 }} />;
}

export { PrivateInsuranceDetails };
